import { useState, useEffect } from "react";
import ServiceFeatureCard from "../components/ServiceCard";
import bgImage from "../assets/home-bg.jpg";
import webImg from "../assets/ser1.jpg";
import aiImg from "../assets/ser2.jpg";
import cloudImg from "../assets/ser3.jpg";
import customImg from "../assets/ser4.jpg";
import appImg from "../assets/ser5.jpg";
import { ChevronLeft, ChevronRight } from "lucide-react";

export default function Home() {
  const services = [
    { title: "Web Development", image: webImg, description: "Responsive websites, high-performance apps, and full-stack custom solutions.", link: "/services/web" },
    { title: "AI & Machine Learning", image: aiImg, description: "Smart automation, NLP, computer vision, and predictive modeling.", link: "/services/ai" },
    { title: "Cloud & DevOps", image: cloudImg, description: "Cloud migration, CI/CD automation, containers, and security.", link: "/services/cloud" },
    { title: "Custom Software", image: customImg, description: "Business software, ERP/CRM platforms, dashboards, and integrations.", link: "/services/custom" },
    { title: "App Development", image: appImg, description: "Cross-platform apps with React Native, Flutter, or native iOS/Android.", link: "/services/app" },
  ];

  const [index, setIndex] = useState(0);
  const [visibleCards, setVisibleCards] = useState(3);
  const [openFaq, setOpenFaq] = useState(null);

  // Detect screen size to adjust number of visible cards
  useEffect(() => {
    const updateCards = () => {
      if (window.innerWidth < 640) setVisibleCards(1);
      else if (window.innerWidth < 1024) setVisibleCards(2);
      else setVisibleCards(3);
    };
    updateCards();
    window.addEventListener("resize", updateCards);
    return () => window.removeEventListener("resize", updateCards);
  }, []);

  // Keep index in bounds when visibleCards changes
  useEffect(() => {
    setIndex((prev) => Math.min(prev, Math.max(0, services.length - visibleCards)));
  }, [visibleCards, services.length]);

  // OPTIONAL: auto-slide every 5s (moves one card; loops)
  useEffect(() => {
    const id = setInterval(() => {
      setIndex((prev) => (prev >= services.length - visibleCards ? 0 : prev + 1));
    }, 5000);
    return () => clearInterval(id);
  }, [services.length, visibleCards]);

  return (
    <>
      {/* Hero Section */}
      <section
        className="relative bg-fixed bg-cover bg-center min-h-[70vh] sm:min-h-screen flex flex-col items-center justify-center text-center px-4 sm:px-6"
        style={{ backgroundImage: `url(${bgImage})` }}
      >
        <div className="absolute inset-0 bg-black opacity-40"></div>
        <div className="relative z-10 text-white">
          <h1 className="text-2xl sm:text-4xl md:text-6xl font-bold mb-4 sm:mb-6 drop-shadow-lg">
            Empowering Innovation with Atronm
          </h1>
          <p className="text-sm sm:text-base md:text-lg max-w-md sm:max-w-xl mx-auto drop-shadow-md mb-6 sm:mb-8">
            Cutting-edge AI, intelligent Web Development, and scalable Custom Tech Solutions that drive real-world results.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 justify-center">
            <a href="#services" className="inline-block bg-blue-600 hover:bg-blue-500 hover:scale-105 hover:shadow-[0_0_25px_rgba(37,99,235,0.7)] text-white font-semibold py-2.5 px-6 sm:py-3 sm:px-8 rounded-full shadow-lg transition-all duration-300">
              View Services
            </a>
            <a href="/contact" className="inline-block bg-blue-600 hover:bg-blue-500 hover:scale-105 hover:shadow-[0_0_25px_rgba(37,99,235,0.7)] text-white font-semibold py-2.5 px-6 sm:py-3 sm:px-8 rounded-full shadow-lg transition-all duration-300">
              Contact Us
            </a>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="bg-violet-50 py-20 border-y-4 border-purple-100 shadow-inner" data-aos="fade-up">
        <div className="text-center mb-12" data-aos="fade-down">
          <h2 className="text-4xl font-bold mb-4">Our Services</h2>
          <p className="max-w-2xl mx-auto text-lg text-gray-600">
            We offer comprehensive solutions in AI, Web Development, DevOps, and Custom Software — tailored to accelerate your digital success.
          </p>
        </div>

        <div className="relative max-w-screen-xl mx-auto px-4 sm:px-6">
          <div className="overflow-hidden">
            <div
              className="flex transition-transform duration-500"
              style={{
                /* ✅ Move one card = 100/N of the row; NOT 100/visibleCards */
                transform: `translateX(-${(index * 100) / services.length}%)`,
                /* Row is N/V viewports wide */
                width: `${(services.length * 100) / visibleCards}%`,
              }}
            >
              {services.map((service, i) => (
                <div key={i} className="p-2 sm:p-3 md:p-4" style={{ /* Each item is one "viewport fraction" of the row */ width: `${100 / services.length}%` }}>
                  <ServiceFeatureCard {...service} className="h-full w-full" />
                </div>
              ))}
            </div>
          </div>

          {/* Navigation Buttons */}
          <button
            onClick={() => setIndex((prev) => Math.max(prev - 1, 0))}
            disabled={index === 0}
            className="absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center bg-white rounded-full shadow-md hover:scale-110 transition disabled:opacity-40"
          >
            <ChevronLeft className="w-5 h-5 sm:w-7 sm:h-7 text-black" />
          </button>

          <button
            onClick={() => setIndex((prev) => Math.min(prev + 1, services.length - visibleCards))}
            disabled={index >= services.length - visibleCards}
            className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center bg-white rounded-full shadow-md hover:scale-110 transition disabled:opacity-40"
          >
            <ChevronRight className="w-5 h-5 sm:w-7 sm:h-7 text-black" />
          </button>
        </div>
      </section>

      {/* How We Help Section */}
      <section className="bg-slate-50 py-20 px-6 border-t border-slate-100 relative overflow-hidden" data-aos="fade-up">
        {/* Glow Blobs */}
        <div className="absolute top-10 left-[-100px] w-[300px] h-[300px] sm:w-[500px] sm:h-[500px] bg-blue-400/5 rounded-full blur-3xl pointer-events-none animate-pulse duration-[10s]"></div>
        <div className="absolute bottom-10 right-[-100px] w-[300px] h-[300px] sm:w-[500px] sm:h-[500px] bg-purple-400/5 rounded-full blur-3xl pointer-events-none animate-pulse duration-[15s]"></div>
        <div className="max-w-6xl mx-auto relative z-10">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <div className="inline-block px-3 py-1 bg-blue-100 text-blue-700 text-xs font-semibold uppercase tracking-wider rounded-full mb-3">
              Our Methodology
            </div>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight">
              How We Help You Succeed
            </h2>
            <p className="text-gray-600 text-base sm:text-lg mt-4">
              At Atronm, we don’t just deliver code—we deliver business results. We partner with you through every phase to ensure your tech accelerates growth.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white border border-slate-100 p-8 rounded-3xl shadow-sm hover:shadow-[0_20px_50px_rgba(59,130,246,0.08)] hover:border-blue-200 transition-all duration-300 group">
              <div className="relative inline-block mb-6">
                <span className="text-3xl font-extrabold text-slate-200 group-hover:text-blue-600 transition-colors duration-300 font-mono">01</span>
                <span className="absolute -bottom-1 left-0 w-full h-[2px] bg-blue-500 scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left"></span>
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3">Discovery & Strategy</h3>
              <p className="text-gray-500 text-sm leading-relaxed">
                We work closely with you to understand operational challenges, outline technical requirements, and define clear, measurable targets.
              </p>
            </div>

            <div className="bg-white border border-slate-100 p-8 rounded-3xl shadow-sm hover:shadow-[0_20px_50px_rgba(168,85,247,0.08)] hover:border-purple-200 transition-all duration-300 group">
              <div className="relative inline-block mb-6">
                <span className="text-3xl font-extrabold text-slate-200 group-hover:text-purple-600 transition-colors duration-300 font-mono">02</span>
                <span className="absolute -bottom-1 left-0 w-full h-[2px] bg-purple-500 scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left"></span>
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3">Bespoke Engineering</h3>
              <p className="text-gray-500 text-sm leading-relaxed">
                Our developers craft clean, high-performance, and secure applications custom-fitted to your company’s unique workflows.
              </p>
            </div>

            <div className="bg-white border border-slate-100 p-8 rounded-3xl shadow-sm hover:shadow-[0_20px_50px_rgba(34,197,94,0.08)] hover:border-green-200 transition-all duration-300 group">
              <div className="relative inline-block mb-6">
                <span className="text-3xl font-extrabold text-slate-200 group-hover:text-green-600 transition-colors duration-300 font-mono">03</span>
                <span className="absolute -bottom-1 left-0 w-full h-[2px] bg-green-500 scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left"></span>
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3">Edge Deployment</h3>
              <p className="text-gray-500 text-sm leading-relaxed">
                We launch your systems on fast cloud infrastructures optimized for latency, security audits, and automated scaling care.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="bg-white py-20 px-6 border-t border-slate-100 relative overflow-hidden" data-aos="fade-up">
        {/* Glow Blobs */}
        <div className="absolute top-20 right-[-150px] w-[300px] h-[300px] sm:w-[500px] sm:h-[500px] bg-blue-300/5 rounded-full blur-3xl pointer-events-none animate-pulse duration-[12s]"></div>
        <div className="absolute bottom-20 left-[-150px] w-[300px] h-[300px] sm:w-[500px] sm:h-[500px] bg-purple-300/5 rounded-full blur-3xl pointer-events-none animate-pulse duration-[18s]"></div>
        <div className="max-w-4xl mx-auto relative z-10">
          <div className="text-center mb-16">
            <div className="inline-block px-3 py-1 bg-purple-100 text-purple-700 text-xs font-semibold uppercase tracking-wider rounded-full mb-3">
              FAQ
            </div>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight">
              Frequently Asked Questions
            </h2>
            <p className="text-gray-600 text-base sm:text-lg mt-4">
              Find quick answers to common questions about Atronm’s custom software development, AI integrations, and support models.
            </p>
          </div>

          <div className="space-y-4">
            {[
              {
                question: "What industries does Atronm specialize in?",
                answer: "We specialize in engineering high-end platforms for FinTech, Healthcare, custom Retail POS networks, robust ledger accounting ERPs, and automated AI billing infrastructures."
              },
              {
                question: "Can Atronm build custom AI agents for existing systems?",
                answer: "Yes, absolutely! We design multi-agent AI ecosystems that integrate seamlessly with your current database models and API channels to automate tasks such as patient eligibility verification, invoice generation, or prior authorizations."
              },
              {
                question: "How do you protect project data and ensure compliance?",
                answer: "Security is built into our core framework. We utilize end-to-end encryption protocols, secure token authentication, role-based access configurations, and build fully compliant systems (such as HIPAA rules for medical data)."
              },
              {
                question: "Do you offer post-launch support and maintenance?",
                answer: "Yes. We provide continuous support contracts that include automated crash reporting, server latency checkups, package upgrades, and feature scaling so your system stays fast and secure over time."
              }
            ].map((faq, i) => (
              <div 
                key={i} 
                className="border border-slate-100 rounded-2xl overflow-hidden hover:border-slate-200 transition-colors duration-200"
              >
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex justify-between items-center p-6 bg-slate-50/50 hover:bg-slate-50 text-left transition-colors duration-200 focus:outline-none"
                >
                  <span className="font-bold text-slate-900 text-base sm:text-lg pr-4">
                    {faq.question}
                  </span>
                  <span className={`text-slate-400 font-light text-2xl transition-transform duration-300 transform ${openFaq === i ? "rotate-45" : ""}`}>
                    ＋
                  </span>
                </button>
                <div 
                  className={`transition-all duration-300 ease-in-out overflow-hidden ${
                    openFaq === i ? "max-h-48 border-t border-slate-100" : "max-h-0"
                  }`}
                >
                  <p className="p-6 text-gray-500 text-sm leading-relaxed bg-white">
                    {faq.answer}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
